import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Autocomplete, Button, FormGroup, Stack, TextField, Typography } from '@mui/material';

import ErrorDisplay from '~/components/Common/ErrorDisplay';
import { formatAffiliationOptionLabel } from '~/components/LeAgentHub/formatAffiliationOptionLabel';

import { Affiliation, Agency, AvailableAffiliations } from '~/types/services/agentHub';

export interface AgencyEditorProps {
  data: Agency;
  onSave: (agencyData: Agency) => void;
  onCancel: () => void;
  loadingState: Utils.FetchingState;
  availableAffiliations: AvailableAffiliations;
  onSelectedAffiliationChange?: (affiliation: Affiliation | null) => void;
}

export default function AgencyEditor({
  data,
  onSave,
  onCancel,
  availableAffiliations,
  loadingState,
  onSelectedAffiliationChange,
}: AgencyEditorProps) {
  const [editedData, setEditedData] = useState<Agency>(data);

  useEffect(() => {
    setEditedData(data);
  }, [data]);

  useEffect(() => {
    const affiliation =
      availableAffiliations?.find((a) => a.id === editedData.affiliationId) ?? null;
    onSelectedAffiliationChange?.(affiliation);
  }, [editedData.affiliationId, availableAffiliations, onSelectedAffiliationChange]);

  const handleAgencyUpdate = useCallback(async () => {
    onSave(editedData);
  }, [editedData, onSave]);

  const selectedAffiliation = useMemo(() => {
    const list = availableAffiliations ?? [];
    const byId = list.find((a) => a.id === editedData.affiliationId);
    if (byId) {
      return byId;
    }
    if (editedData.affiliation) {
      return list.find((a) => a.name === editedData.affiliation) ?? null;
    }
    return null;
  }, [availableAffiliations, editedData.affiliation, editedData.affiliationId]);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()} style={{ minWidth: '500px' }}>
        <Stack mt={2} direction="column" spacing={2}>
          <FormGroup>
            <TextField
              label="Name"
              value={editedData.name}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label={editedData.businessCodeType}
              value={editedData.businessCodeValue}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, businessCodeValue: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Autocomplete
              value={selectedAffiliation}
              options={availableAffiliations}
              getOptionLabel={formatAffiliationOptionLabel}
              onChange={(_, value) =>
                setEditedData({
                  ...editedData,
                  affiliationId: value?.id,
                  affiliation: value?.name ?? '',
                })
              }
              renderInput={(params) => <TextField {...params} label="Affiliation" />}
            />
          </FormGroup>
          <Typography mt={2} variant="h6" sx={{ gridColumn: 'span 2' }}>
            Address
          </Typography>

          <FormGroup>
            <TextField
              label="Street"
              value={editedData.address}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, address: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="City"
              value={editedData.city}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, city: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <TextField
              label="Postal Code"
              value={editedData.postcode}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, postcode: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="State"
              value={editedData.state}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, state: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <TextField
              label="Country"
              value={editedData.country}
              required
              fullWidth
              onChange={(e) => setEditedData({ ...editedData, country: e.target.value })}
            />
          </FormGroup>
          {loadingState == 'failed' && (
            <ErrorDisplay message="There was an error updating the agency" severity="error" />
          )}
        </Stack>
      </form>
      <Stack mt={2} display="flex" justifyContent="space-around" direction="row">
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button disabled={loadingState === 'loading'} variant="contained" onClick={handleAgencyUpdate}>
          {loadingState === 'loading' ? 'Saving...' : 'Save'}
        </Button>
      </Stack>
    </>
  );
}
